import { EmbedSettingsResponseDto, ProcessCard, UpdateEmbedSettingsRequestDto } from "@/lib/interfaces/embed";
import { Box, Button, Flex, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Copy, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../ui/components/ui/accordion";
import { Checkbox } from "../../ui/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/components/ui/form";
import { Input } from "../../ui/components/ui/input";
import { Textarea } from "../../ui/components/ui/textarea";
import { IFrameSettingsJobPreview } from "./IFrameSettingsJobPreview";
import { IframeSettingsPreviewProvider, useIframeSettingsPreview } from "./IframeSettingsPreviewContext";

const CopyIframe = ({ organizationId }: { organizationId: string }) => {
  const [isCopied, setIsCopied] = useState(false);

  // Responsive iframe code
  const iframeLink = `<iframe frameborder="0" id="karriereportal-iframe" src="${window.location.protocol}//${window.location.host}/job-angebote/iframe/${organizationId}" width="100%" style="border: none; overflow: hidden;"></iframe>
  <script src="https://kandidat-io-integrations.s3.eu-central-1.amazonaws.com/scripts/kandidat-external-embed-script.js"></script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(iframeLink);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <Box className="mt-8 max-w-2xl">
      <div className="relative">
        <label className="text-sm font-bold">Karriereportallink</label>
        <Textarea value={iframeLink} readOnly className="pr-12 resize-none" rows={3} />
        <Button onClick={handleCopy} className="absolute top-2 right-2 p-2 h-8 w-8" variant="ghost" size="icon">
          {isCopied ? <Check className="h-6 w-6 text-green-600" /> : <Copy className="h-6 w-6" />}
        </Button>
      </div>
    </Box>
  );
};

const IFrameSettings = () => {
  const { apiRequest } = useApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [embedSettings, setEmbedSettings] = useState<EmbedSettingsResponseDto | null>(null);

  useEffect(() => {
    const fetchEmbedSettings = async () => {
      setIsLoading(true);
      const res = await apiRequest<EmbedSettingsResponseDto>(`embed/settings/`, "GET");
      if (res.data) {
        setEmbedSettings(res.data);
      }
      setIsLoading(false);
    };

    fetchEmbedSettings();
  }, [apiRequest]);

  return (
    <div className="w-full h-full bg-white p-6">
      <h2 className="text-2xl font-bold mb-6">Karriereportal Einstellungen</h2>
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600 text-sm leading-relaxed">
          Mit dem Karriereportal können Sie Ihre Stellenangebote ganz unkompliziert direkt auf Ihrer eigenen Website
          präsentieren. Die Einbindung funktioniert über einen kurzen HTML-Code („iframe“), den Sie einmalig auf Ihrer
          Website hinterlegen. So wird das Portal nahtlos in Ihre Seite integriert – Besucher sehen die Stellenanzeigen
          genau dort, wo Sie es möchten.
          <br />
          Im Bereich der visuellen Einstellungen können Sie das Erscheinungsbild des Portals individuell an Ihr Design
          anpassen. Zum Beispiel wählen Sie Ihre Akzentfarbe aus oder entscheiden, ob Bilder angezeigt werden sollen.
          Alle Änderungen, die Sie hier vornehmen, werden automatisch im eingebetteten Portal übernommen.
          <br />
          Um eine Stelle auf Ihrer Website zu veröffentlichen, gehen Sie einfach in Ihre Stellenübersicht, aktivieren
          dort das gewünschte Jobangebot und wählen im erscheinenden Fenster die Option *„Karriereportal aktivieren“*.
          Die Stelle ist sofort online sichtbar. Ebenso leicht können Sie Angebote wieder deaktivieren.
          <br />
          So behalten Sie die volle Kontrolle über Ihre Stellenanzeigen – ohne technischen Aufwand.
        </p>
      </div>
      {isLoading ? (
        <Flex className="justify-center w-full h-52 items-center">
          <Spinner size="xl" color="primary.500" />
        </Flex>
      ) : (
        <>
          {embedSettings ? (
            <IframeSettingsPreviewProvider embedSettings={embedSettings}>
              <Tabs position="relative" height="100%" flex="1" display="flex" flexDirection="column" isLazy>
                <div className="flex gap-12 flex-1 min-h-0 mt-4">
                  <div className="flex-1 min-w-0 max-w-xl flex flex-col">
                    <TabList>
                      <Tab sx={{ color: "#7C8080" }} key={1}>
                        Designanpassungen
                      </Tab>
                      <Tab sx={{ color: "#7C8080" }} key={2}>
                        Texte und Inhalt
                      </Tab>
                    </TabList>
                    <TabPanels display="flex" flexDirection="column" flex="1" pt={4}>
                      <TabPanel key={1} p={0}>
                        <DesignSettings embedSettings={embedSettings} />
                      </TabPanel>
                      <TabPanel key={2} p={0}>
                        <TextSettings embedSettings={embedSettings} />
                      </TabPanel>
                    </TabPanels>
                  </div>
                  <IFrameSettingsJobPreview />
                </div>
              </Tabs>
            </IframeSettingsPreviewProvider>
          ) : (
            <Flex className="justify-center w-full h-52 items-center">
              <Spinner size="xl" color="primary.500" />
            </Flex>
          )}
        </>
      )}
    </div>
  );
};

const TextSettings = ({ embedSettings }: { embedSettings: EmbedSettingsResponseDto }) => {
  const { apiRequest } = useApiRequest();
  const { setTextDraft } = useIframeSettingsPreview();

  // Zod schemas for validation
  const processSchema = z.object({
    title: z.string().min(1, "Titel ist erforderlich"),
    description: z.string().min(1, "Beschreibung ist erforderlich"),
  });

  const contactSchema = z.object({
    title: z.string().min(1, "Titel ist erforderlich"),
    description: z.string().min(1, "Beschreibung ist erforderlich"),
  });

  const processCardSchema = z.object({
    title: z.string().min(1, "Titel ist erforderlich"),
    description: z.string().min(1, "Beschreibung ist erforderlich"),
    info: z.string().optional(),
  });
  const heroSchema = z.object({
    heroEyebrowText: z.string().optional(),
  });

  // Form for process settings
  const processForm = useForm<z.infer<typeof processSchema>>({
    resolver: zodResolver(processSchema),
    defaultValues: {
      title: embedSettings.process?.title || "",
      description: embedSettings.process?.description || "",
    },
  });

  // Form for contact settings
  const contactForm = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      title: embedSettings.contact?.title || "",
      description: embedSettings.contact?.description || "",
    },
  });

  // Form for new process card
  const newProcessCardForm = useForm<z.infer<typeof processCardSchema>>({
    resolver: zodResolver(processCardSchema),
    defaultValues: {
      title: "",
      description: "",
      info: "",
    },
  });

  const heroForm = useForm<z.infer<typeof heroSchema>>({
    resolver: zodResolver(heroSchema),
    defaultValues: {
      heroEyebrowText: embedSettings.heroEyebrowText ?? "",
    },
  });

  // State for managing process cards and editing
  const [processCards, setProcessCards] = useState(embedSettings.processCards || []);
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const showContactSettings = false;

  // Form for editing a process card (only one at a time)
  const editCardForm = useForm<z.infer<typeof processCardSchema>>({
    resolver: zodResolver(processCardSchema),
    defaultValues: {
      title: "",
      description: "",
      info: "",
    },
  });

  // Set form values when editing card changes
  useEffect(() => {
    if (editingCard) {
      const card = processCards.find((c) => c._id === editingCard);
      if (card) {
        editCardForm.reset({
          title: card.title,
          description: card.description,
          info: card.info || "",
        });
      }
    }
  }, [editingCard, processCards, editCardForm]);

  // Submit handlers
  const onProcessSubmit = async (values: z.infer<typeof processSchema>) => {
    await apiRequest(`embed/settings/process`, "PATCH", {
      body: values,
      toast: { toastText: "Prozess-Einstellungen erfolgreich aktualisiert" },
    });
  };

  const onContactSubmit = async (values: z.infer<typeof contactSchema>) => {
    await apiRequest(`embed/settings/contact`, "PATCH", {
      body: values,
      toast: { toastText: "Kontakt-Einstellungen erfolgreich aktualisiert" },
    });
  };

  const onProcessCardSubmit = async (cardId: string, values: z.infer<typeof processCardSchema>) => {
    await apiRequest(`embed/settings/process-cards/${cardId}`, "PATCH", {
      body: values,
      toast: { toastText: "Prozess-Karte erfolgreich aktualisiert" },
    });
    setEditingCard(null);
    // Update local state
    setProcessCards((prev) => prev.map((card) => (card._id === cardId ? { ...card, ...values } : card)));
  };

  const onNewProcessCardSubmit = async (values: z.infer<typeof processCardSchema>) => {
    const res = await apiRequest(`embed/settings/process-cards`, "POST", {
      body: values,
      toast: { toastText: "Neue Prozess-Karte erfolgreich erstellt" },
    });
    if (res.data) {
      setProcessCards((prev) => [...prev, res.data as ProcessCard]);
      newProcessCardForm.reset();
      setIsAddingNew(false);
    }
  };

  const onHeroSubmit = async (values: z.infer<typeof heroSchema>) => {
    await apiRequest(`embed/settings/`, "PATCH", {
      body: {
        heroEyebrowText: values.heroEyebrowText?.trim() || "",
      } as UpdateEmbedSettingsRequestDto,
      toast: { toastText: "Hero-Text erfolgreich aktualisiert" },
    });
  };

  const deleteProcessCard = async (cardId: string) => {
    await apiRequest(`embed/settings/process-cards/${cardId}`, "DELETE", {
      toast: { toastText: "Prozess-Karte erfolgreich gelöscht" },
    });
    setProcessCards((prev) => prev.filter((card) => card._id !== cardId));
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const flush = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setTextDraft({
          heroEyebrowText: heroForm.getValues("heroEyebrowText") ?? "",
          processTitle: processForm.getValues("title"),
          processDescription: processForm.getValues("description"),
          processCards,
          contact: {
            title: contactForm.getValues("title"),
            description: contactForm.getValues("description"),
          },
        });
      }, 500);
    };
    const s1 = processForm.watch(flush);
    const s2 = contactForm.watch(flush);
    const s3 = heroForm.watch(flush);
    flush();
    return () => {
      clearTimeout(timer);
      s1.unsubscribe();
      s2.unsubscribe();
      s3.unsubscribe();
    };
  }, [contactForm, processCards, processForm, heroForm, setTextDraft]);

  return (
    <div className="space-y-8 max-w-md">
      <Box>
        <h3 className="text-lg font-semibold mb-4">Hero-Einstellungen</h3>
        <Form {...heroForm}>
          <form onSubmit={heroForm.handleSubmit(onHeroSubmit)} className="space-y-4">
            <FormField
              control={heroForm.control}
              name="heroEyebrowText"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Text über der Headline</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} placeholder="z.B. Pflege mit Herz" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" colorScheme="primary" className="flex gap-2">
              <Save className="w-4 h-4" />
              Speichern
            </Button>
          </form>
        </Form>
      </Box>

      {/* Process Settings */}
      <Box>
        <h3 className="text-lg font-semibold mb-4">Prozess-Einstellungen</h3>
        <Form {...processForm}>
          <form onSubmit={processForm.handleSubmit(onProcessSubmit)} className="space-y-4">
            <FormField
              control={processForm.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Titel</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z.B. Unser Bewerbungsprozess" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={processForm.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Beschreibung</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="w-full"
                      rows={3}
                      placeholder="Beschreiben Sie hier Ihren Bewerbungsprozess..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" colorScheme="primary" className="flex gap-2">
              <Save className="w-4 h-4" />
              Speichern
            </Button>
          </form>
        </Form>
      </Box>

      {/* Process Cards */}
      <Box>
        <h3 className="text-lg font-semibold mb-4">Prozess-Schritte</h3>
        <Accordion type="single" collapsible className="w-full space-y-2">
          {processCards.map((card) => (
            <AccordionItem key={card._id} value={card._id} className="border rounded-lg">
              <AccordionTrigger className="px-4 py-3 text-left">
                <span className="font-medium">
                  {card.orderNumber}. {card.title}
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                {editingCard === card._id ? (
                  <Form {...editCardForm}>
                    <form
                      onSubmit={editCardForm.handleSubmit((values: any) => onProcessCardSubmit(card._id, values))}
                      className="space-y-4"
                    >
                      <FormField
                        control={editCardForm.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-bold">Titel</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editCardForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-bold">Beschreibung</FormLabel>
                            <FormControl>
                              <Textarea {...field} className="w-full" rows={3} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editCardForm.control}
                        name="info"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-bold">Zusätzliche Info (optional)</FormLabel>
                            <FormControl>
                              <Textarea {...field} className="w-full" rows={2} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex gap-2">
                        <Button type="submit" colorScheme="primary" size="sm">
                          <Save className="w-4 h-4 mr-2" />
                          Speichern
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setEditingCard(null)}>
                          Abbrechen
                        </Button>
                      </div>
                    </form>
                  </Form>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">{card.description}</p>
                    {card.info && <p className="text-xs text-gray-500">{card.info}</p>}
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditingCard(card._id)}>
                        Bearbeiten
                      </Button>
                      <Button size="sm" variant="outline" colorScheme="red" onClick={() => deleteProcessCard(card._id)}>
                        Löschen
                      </Button>
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Add New Process Card */}
        {isAddingNew ? (
          <Box className="mt-4 p-4 border border-dashed border-gray-300 rounded-lg">
            <h4 className="font-medium mb-3">Neuen Prozess-Schritt hinzufügen</h4>
            <Form {...newProcessCardForm}>
              <form onSubmit={newProcessCardForm.handleSubmit(onNewProcessCardSubmit)} className="space-y-4">
                <FormField
                  control={newProcessCardForm.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">Titel</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="z.B. Erstgespräch" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={newProcessCardForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">Beschreibung</FormLabel>
                      <FormControl>
                        <Textarea {...field} className="w-full" rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={newProcessCardForm.control}
                  name="info"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">Zusätzliche Info (optional)</FormLabel>
                      <FormControl>
                        <Textarea {...field} className="w-full" rows={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2">
                  <Button type="submit" colorScheme="primary" size="sm">
                    Hinzufügen
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingNew(false);
                      newProcessCardForm.reset();
                    }}
                  >
                    Abbrechen
                  </Button>
                </div>
              </form>
            </Form>
          </Box>
        ) : (
          <Button variant="outline" className="mt-4 w-full border-dashed" onClick={() => setIsAddingNew(true)}>
            + Neuen Prozess-Schritt hinzufügen
          </Button>
        )}
      </Box>

      {/* Contact Settings */}
      {showContactSettings ? (
        <Box>
          <h3 className="text-lg font-semibold mb-4">Kontakt-Einstellungen</h3>
          <Form {...contactForm}>
            <form onSubmit={contactForm.handleSubmit(onContactSubmit)} className="space-y-4">
              <FormField
                control={contactForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Titel</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="z.B. Haben Sie Fragen?" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={contactForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Beschreibung</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="w-full"
                        rows={3}
                        placeholder="Kontaktinformationen und Ansprechpartner..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" colorScheme="primary" className="flex gap-2">
                <Save className="w-4 h-4" />
                Speichern
              </Button>
            </form>
          </Form>
        </Box>
      ) : null}
    </div>
  );
};

const DesignSettings = ({ embedSettings }: { embedSettings: EmbedSettingsResponseDto }) => {
  const { apiRequest } = useApiRequest();
  const { setDesignDraft } = useIframeSettingsPreview();
  const { user } = useUser();

  // Define form schema with zod
  const formSchema = z.object({
    color: z.string().optional(),
    showImage: z.boolean(),
    customCss: z.string().optional(),
  });

  // Define the form with react-hook-form and zodResolver
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      color: embedSettings.color ?? "#000000",
      showImage: embedSettings.showImage ?? true,
      customCss: embedSettings.customCss ?? "",
    },
  });

  type FormValues = z.infer<typeof formSchema>;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const flush = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const values = form.getValues();
        setDesignDraft({
          color: values.color ?? "#000000",
          showImage: values.showImage ?? embedSettings.showImage ?? true,
          customCss: values.customCss ?? "",
        });
      }, 500);
    };
    const sub = form.watch(flush);
    flush();
    return () => {
      clearTimeout(timer);
      sub.unsubscribe();
    };
  }, [embedSettings.showImage, form, setDesignDraft]);

  async function onSubmit(values: FormValues) {
    const req: UpdateEmbedSettingsRequestDto = {
      color: values.color,
      showImage: values.showImage,
      customCss: values.customCss,
    };

    await apiRequest<EmbedSettingsResponseDto>(`embed/settings/`, "PATCH", {
      body: req,
      toast: { toastText: "Karriereportal Einstellungen erfolgreich aktualisiert" },
    });
  }

  return (
    <div className="max-w-md">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col gap-6">
          <Box>
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem className="gap-4 mb-5 items-center flex">
                  <FormLabel className="w-full font-bold mt-4 text-md">Akzentfarbe wählen</FormLabel>
                  <Flex className="bg-yellow-100 gap-4 w-full px-3 py-1 rounded items-center">
                    <input
                      type="color"
                      className="w-14 h-8 p-0 rounded-lg bg-transparent cursor-pointer"
                      {...field}
                      value={field.value || "#000000"}
                    />
                    <p>{field.value || "#000000"}</p>
                    <FormMessage />
                  </Flex>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showImage"
              render={({ field }) => (
                <FormItem className="flex gap-4 mb-4 w-full items-center">
                  <FormLabel className="font-bold text-md">Bild anzeigen</FormLabel>
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(state) => field.onChange(state === true)}
                      className="w-4 h-4 m-0"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* CSS Classes Reference */}
            <Box className="mt-6 mb-4">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="css-classes">
                  <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-gray-900">
                    Erweiterte Entwickler Einstellungen
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <FormField
                      control={form.control}
                      name="customCss"
                      render={({ field }) => (
                        <FormItem className="flex gap-4 mb-4 w-full items-center">
                          <FormLabel className="font-bold text-md">Custom CSS</FormLabel>
                          <Textarea {...field} className="w-full" />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 mb-3">
                        Diese CSS-Klassen können im Custom CSS Feld verwendet werden, um Bereiche der Stellenanzeige
                        gezielt anzupassen. Sie gelten in der Live-Vorschau und auf der öffentlichen Stellenanzeige-URL.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono mb-3">
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-800 mb-2">Root & Seitenbereiche:</div>
                          <div className="text-blue-700">• .embed-job-root</div>
                          <div className="text-blue-700">• .embed-job-demo-banner</div>
                          <div className="text-blue-700">• .embed-job-body-section</div>
                          <div className="text-blue-700">• .embed-job-footer</div>

                          <div className="font-semibold text-gray-800 mb-2 mt-4">Kopf & Hero:</div>
                          <div className="text-blue-700">• .embed-job-header</div>
                          <div className="text-blue-700">• .embed-job-header-cta</div>
                          <div className="text-blue-700">• .embed-job-hero</div>
                          <div className="text-blue-700">• .embed-job-hero-cta</div>
                          <div className="text-blue-700">• .embed-job-hero-media</div>
                        </div>
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-800 mb-2">Ähnliche Stellen & Bewerbung:</div>
                          <div className="text-blue-700">• .embed-job-similar</div>
                          <div className="text-blue-700">• .embed-job-similar-card</div>
                          <div className="text-blue-700">• .embed-job-contact</div>
                          <div className="text-blue-700">• .embed-job-application</div>
                          <div className="text-blue-700">• .embed-job-form</div>
                          <div className="text-blue-700">• .embed-job-submit</div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        In der Live-Vorschau wird Custom CSS per <code className="text-[11px]">@scope</code> auf den
                        Vorschau-Container (<code className="text-[11px]">.iframe-settings-job-preview-root</code>)
                        begrenzt; auf der öffentlichen Seite wirkt das Stylesheet auf die gesamte Ansicht.
                      </p>
                      <div className="mt-1 p-2 bg-blue-50 rounded text-xs text-blue-800">
                        <strong>Beispiel:</strong>{" "}
                        <code>.embed-job-hero-cta &#123; box-shadow: 0 4px 14px rgba(0,0,0,0.15); &#125;</code>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Box>
          </Box>

          <Box className="flex justify-start">
            <Button
              colorScheme="primary"
              color="white"
              className="flex gap-2 p-2 text-white"
              variant="solid"
              type="submit"
            >
              <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
                <Save />
              </Box>
              <span>Speichern</span>
            </Button>
          </Box>
        </form>
      </Form>
      <CopyIframe organizationId={user?.organizationId || "67e9340a14cf2c909ff19b9b"} />
    </div>
  );
};

export { IFrameSettings };
